if (!customElements.get("drawer-select")) {
  customElements.define(
    "drawer-select",
    class DrawerSelect extends DrawerBase {
      // ── Instance state ──
      #optionId;
      #currentVariant;
      #optionTitleLabel;
      #triggerLabel;
      #options;
      #optionName;
      #form;
      #inputType;

      // ── Required overrides ──
      get container() {
        return this.querySelector(".wt-select__drawer");
      }
      get trigger() {
        return this.querySelector(".wt-select__trigger");
      }
      get closeButton() {
        return this.querySelector(".wt-select__drawer__close");
      }

      // ── Optional overrides ──
      get containerOpenClass() {
        return "wt-select__drawer--open";
      }
      get bodyOverlayClasses() {
        return ["page-overlay-on", "wt-select-opened"];
      }
      get tabindexTargets() {
        return ".wt-select__item, .wt-select__drawer__close";
      }

      onClose() {
        this.trigger?.focus();
      }

      // ── Lifecycle ──
      onConnected() {
        this.#optionId = this.dataset.optionId;
        this.#currentVariant = this.dataset.currentVariant;
        this.#optionTitleLabel = this.querySelector(
          ".wt-product__option__title .value"
        );
        this.#triggerLabel = this.trigger?.querySelector(
          ".wt-select__trigger__label"
        );
        this.#options = this.querySelectorAll(
          ".wt-select__item:not(.wt-select__item--disabled)"
        );
        this.#optionName = this.querySelector(
          ".wt-product__option__title .label"
        )?.innerText;
        this.#form = document.querySelector(
          `form[data-type=add-to-cart-form]:has(input[name='id'][value='${this.#currentVariant}'])`
        );
        this.#inputType = this.dataset.inputType;

        if (this.#inputType === "dropdown") {
          this._setupOptionListeners();
          this.preselectFirstOption();
        } else if (this.#inputType === "text") {
          this._setupTextListeners();
        } else {
          this._setupCheckboxListeners();
        }
      }

      // ── Option selection (dropdown type) ──
      _setupOptionListeners() {
        this._boundSetOption = this.setOption.bind(this);
        this.#options.forEach((option) => {
          option.addEventListener("click", this._boundSetOption);
        });
      }

      setOption(event) {
        const target = event.currentTarget || event;
        const value = target.dataset.value.trim();

        let span = this.#triggerLabel?.querySelector(".value");
        if (!span && this.#triggerLabel) {
          this.#triggerLabel.textContent = "";
          span = document.createElement("span");
          span.className = "value";
          this.#triggerLabel.appendChild(span);
        }
        if (span) span.textContent = value;
        if (this.#optionTitleLabel) this.#optionTitleLabel.textContent = value;

        this.#options.forEach((option) => {
          option.classList.remove("wt-select__item--current");
          option.setAttribute("aria-selected", "false");
        });

        target.classList.add("wt-select__item--current");
        target.setAttribute("aria-selected", "true");

        this.updateHiddenInput(value);
        this.closeDrawer();
      }

      preselectFirstOption() {
        const currentOption = this.container?.querySelector(
          ".wt-select__item--current"
        );
        if (currentOption) {
          this.setOption(currentOption);
        } else if (this.#options[0]) {
          this.setOption(this.#options[0]);
        }
      }

      // ── Checkbox type ──
      _setupCheckboxListeners() {
        this._inputCheckbox = this.querySelector(".form-checkbox__input");
        this._boundSetCheckbox = this._setCheckboxOption.bind(this);
        if (this._inputCheckbox) {
          this._inputCheckbox.addEventListener("click", this._boundSetCheckbox);
        }
      }

      _setCheckboxOption(event) {
        const target = event.currentTarget || event;
        const value = target.checked ? target.value : "";
        this.updateHiddenInput(value);
      }

      // ── Text type ──
      _setupTextListeners() {
        this._inputText = this.querySelector(".wt-product__option__text");
        this._boundSetText = this._setTextOption.bind(this);
        if (this._inputText) {
          this._inputText.addEventListener("change", this._boundSetText);
        }
      }

      _setTextOption(event) {
        const target = event.currentTarget || event;
        const value = target.value;
        this.updateHiddenInput(value);
      }

      // ── Form binding ──
      updateHiddenInput(value) {
        let hiddenInput = this.#form?.querySelector(
          `input[type="hidden"][name="properties[${this.#optionName}]"]`
        );
        if (!hiddenInput && this.#form) {
          hiddenInput = document.createElement("input");
          hiddenInput.type = "hidden";
          hiddenInput.name = `properties[${this.#optionName}]`;
          this.#form.appendChild(hiddenInput);
        }
        if (hiddenInput) hiddenInput.value = value;
      }

      // ── Cleanup ──
      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.#inputType === "dropdown") {
          this.#options?.forEach((option) => {
            option.removeEventListener("click", this._boundSetOption);
          });
        } else if (this.#inputType === "text" && this._inputText) {
          this._inputText.removeEventListener("change", this._boundSetText);
        } else if (this._inputCheckbox) {
          this._inputCheckbox.removeEventListener("click", this._boundSetCheckbox);
        }
      }
    }
  );
}
